import { isEmpty } from 'lodash';
import { Inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { DeviceUserFormControlName, MyDeviceFormControlName } from '../models';
import { MyDevicesFormState } from '../types/my-devices-form-builder-service.type';
import { MAX_SERIAL_NUMBER_CHARS_WHEN_NEED_VALIDATE } from '@core/constants/user-device';
import { USER_CONFIG, UserConfig } from '@core/modules/user/user.config';
import { MyDeviceFormService } from './my-device-form.service';
import { UserDevicesFormValidationErrorKye } from '../models/user-devices-form-validation-error-key.enum';

@Injectable({
  providedIn: 'any'
})
export class NewDeviceRegistrationFormService {
  constructor(
    @Inject(USER_CONFIG) private readonly config: UserConfig,
    private readonly myDeviceFormService: MyDeviceFormService
  ) {}

  buildNewDeviceFormGroup(
    formState: MyDevicesFormState,
    serial?: string,
    passphrase?: string
  ): FormGroup {
    const deviceFormGroup = new FormGroup({});

    deviceFormGroup.addControl(
      DeviceUserFormControlName.SERIAL,
      new FormControl(serial, {
        validators: [Validators.required],
        asyncValidators: [this.checkSerialValidator(deviceFormGroup, formState)],
        updateOn: 'submit'
      })
    );

    if (passphrase) {
      deviceFormGroup.addControl(
        DeviceUserFormControlName.PASSPHRASE,
        new FormControl(passphrase, {
          validators: [Validators.required]
        })
      );
    }

    return deviceFormGroup;
  }

  private checkSerialValidator(
    deviceFormGroup: FormGroup,
    formState: MyDevicesFormState
  ): AsyncValidatorFn {
    return (serialControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!formState.isSubmitting) {
        return of({ pending: true });
      }

      if (formState.hasError) {
        return of({ pending: true });
      }

      if (serialControl.value?.length < MAX_SERIAL_NUMBER_CHARS_WHEN_NEED_VALIDATE) {
        return of({ ...this.config.ERRORS.SMALL_SERIAL });
      }

      if (this.hasDuplicatedSerial(serialControl)) {
        return of({ [UserDevicesFormValidationErrorKye.NOT_FREE]: 'notfree' });
      }

      return of(deviceFormGroup.value).pipe(
        take(1),
        switchMap(({ serial, passphrase }) =>
          this.myDeviceFormService.checkSerial({ serial, passphrase })
        )
      );
    };
  }

  private hasDuplicatedSerial(control: AbstractControl): boolean {
    return (
      [
        ...((
          control.root?.get(MyDeviceFormControlName.REGISTERED_DEVICES) as FormArray
        )?.getRawValue() || []),
        ...((control.root?.get(MyDeviceFormControlName.NEW_DEVICES) as FormArray)?.getRawValue() ||
          [])
      ].filter((device) => device.serial === control.value)?.length > 1
    );
  }
}
